import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Progress } from "@/components/ui/progress";
import { HandHeart, Repeat, Home, GraduationCap, Building2, Heart } from "lucide-react";

const TIERS = [
  { key: "one_time", label: "One-Time", Icon: HandHeart, description: "A single gift to fuel our next season." },
  { key: "monthly", label: "Monthly", Icon: Repeat, description: "Sustaining monthly support — the backbone of our work." },
  { key: "sponsor_family", label: "Sponsor a Family", Icon: Home, description: "$150/mo covers a family's coaching + retreat." },
  { key: "sponsor_youth", label: "Sponsor a Youth", Icon: Heart, description: "$75/mo covers mentorship, tutoring & meals." },
  { key: "scholarship", label: "Scholarship Fund", Icon: GraduationCap, description: "Send a student to college, trade school or camp." },
  { key: "corporate", label: "Corporate Partner", Icon: Building2, description: "Sponsor a program, event or cohort as a business." },
];

const AMOUNTS = [25, 50, 100, 250, 500];

export default function Donate() {
  const [tier, setTier] = useState("one_time");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");
  const [form, setForm] = useState({ donor_name: "", donor_email: "", dedicated_to: "", notes: "" });
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/donation-progress").then((r) => setProgress(r.data.campaigns || [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const finalAmount = Number(custom) > 0 ? Number(custom) : Number(amount);
    if (!finalAmount || finalAmount <= 0) {
      toast.error("Please choose a donation amount.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/donation-intents", {
        tier,
        amount: finalAmount,
        donor_name: form.donor_name,
        donor_email: form.donor_email,
        is_recurring: tier === "monthly",
        dedicated_to: form.dedicated_to || null,
        notes: form.notes || null,
      });
      toast.success("Thank you! Your intent was saved. Payment processing coming soon.");
      setForm({ donor_name: "", donor_email: "", dedicated_to: "", notes: "" });
      setCustom("");
    } catch (err) {
      toast.error("Could not save donation intent.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="donate-page">
      <section className="section">
        <div className="container-editorial">
          <div className="eyebrow">Donation center</div>
          <h1 className="mt-4 font-serif text-5xl leading-[1.05] text-ink md:text-6xl">
            Every dollar is a <em className="not-italic text-clay">key handed over</em>.
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-muted">
            KEYZ Foundation Inc. is a registered 501(c)(3). Every gift is tax-deductible and 92¢ of every dollar
            goes directly to programs and participants.
          </p>

          <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
            {/* LEFT: Tier + Amount */}
            <div className="lg:col-span-7">
              <div className="rounded-3xl border border-clay/15 bg-white p-8 md:p-10">
                <div className="eyebrow">1 · Choose how</div>
                <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
                  {TIERS.map((t) => {
                    const active = tier === t.key;
                    return (
                      <button
                        type="button"
                        key={t.key}
                        data-testid={`tier-${t.key}`}
                        onClick={() => setTier(t.key)}
                        className={`text-left rounded-2xl border-2 p-4 transition-colors ${
                          active ? "border-clay bg-oat-100" : "border-clay/10 hover:border-clay/40"
                        }`}
                      >
                        <t.Icon className={`h-5 w-5 ${active ? "text-clay" : "text-ink-muted"}`} />
                        <div className="mt-3 font-serif text-lg text-ink">{t.label}</div>
                        <p className="mt-1 text-xs text-ink-muted">{t.description}</p>
                      </button>
                    );
                  })}
                </div>

                <div className="mt-10 eyebrow">2 · Choose amount</div>
                <div className="mt-4 flex flex-wrap gap-3">
                  {AMOUNTS.map((a) => (
                    <button
                      type="button"
                      key={a}
                      data-testid={`amount-${a}`}
                      onClick={() => { setAmount(a); setCustom(""); }}
                      className={`rounded-full border-2 px-5 py-2.5 font-semibold transition-colors ${
                        amount === a && !custom ? "border-clay bg-clay text-oat" : "border-clay/25 text-ink hover:border-clay"
                      }`}
                    >
                      ${a}
                    </button>
                  ))}
                  <div className="flex items-center gap-2 rounded-full border-2 border-clay/25 px-4 py-1.5">
                    <span className="text-ink-muted">$</span>
                    <input
                      type="number"
                      min="1"
                      placeholder="Other"
                      data-testid="amount-custom"
                      value={custom}
                      onChange={(e) => setCustom(e.target.value)}
                      className="w-24 bg-transparent text-ink outline-none"
                    />
                  </div>
                </div>

                <form onSubmit={submit} className="mt-10">
                  <div className="eyebrow">3 · Your info</div>
                  <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="field-label">Name *</label>
                      <input required data-testid="donor-name" className="field-input" value={form.donor_name} onChange={(e) => setForm({ ...form, donor_name: e.target.value })} />
                    </div>
                    <div>
                      <label className="field-label">Email *</label>
                      <input required type="email" data-testid="donor-email" className="field-input" value={form.donor_email} onChange={(e) => setForm({ ...form, donor_email: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label">Dedicate this gift (optional)</label>
                      <input data-testid="donor-dedication" className="field-input" placeholder="In memory of… / In honor of…" value={form.dedicated_to} onChange={(e) => setForm({ ...form, dedicated_to: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="field-label">Notes</label>
                      <textarea rows={3} data-testid="donor-notes" className="field-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                    </div>
                  </div>
                  <div className="mt-6 rounded-xl border border-clay/15 bg-oat-100 p-4 text-sm text-ink-muted">
                    Payment processing is coming soon. For now, this saves your donation intent — a KEYZ team member will
                    follow up to complete your gift.
                  </div>
                  <div className="mt-6 flex justify-end">
                    <button type="submit" disabled={loading} data-testid="donate-submit" className="btn-primary">
                      {loading ? "Saving…" : `Give $${Number(custom) > 0 ? custom : amount}`}
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* RIGHT: Campaigns */}
            <div className="lg:col-span-5">
              <div className="rounded-3xl border border-sage/25 bg-sage p-8 text-oat md:p-10">
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-oat/80">Active campaigns</div>
                <h2 className="mt-3 font-serif text-3xl leading-tight">Where your gift goes.</h2>
                <div className="mt-8 space-y-6">
                  {progress.map((c) => {
                    const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
                    return (
                      <div key={c.key} data-testid={`campaign-${c.key}`}>
                        <div className="flex items-center justify-between text-sm">
                          <div className="font-semibold">{c.name}</div>
                          <div className="text-oat/80">${c.raised.toLocaleString()} / ${c.goal.toLocaleString()}</div>
                        </div>
                        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-oat/20">
                          <div className="h-full rounded-full bg-oat" style={{ width: `${pct}%` }} />
                        </div>
                        <div className="mt-1.5 text-xs text-oat/70">{c.donors} donors · {pct}% funded</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
